<template>
  <div class="modal-content">
    <div class="modal-header">
      <h5 class="modal-title">STL Preview</h5>
      <button
        type="button"
        class="btn-close"
        data-bs-dismiss="modal"
        aria-label="Close"
      ></button>
    </div>
    <div class="modal-body">
      <div
        v-if="contentStore.stlLoadingState === STLLoadingState.Loading"
        class="loading-container"
      >
        <div id="loader">
          <div class="dot dot-1"></div>
          <div class="dot dot-2"></div>
          <div class="dot dot-3"></div>
          <p class="loading-status">{{ loadingText }}</p>
        </div>
        <div
          v-if="contentStore.isLargeModelGeneration"
          class="loading-copy"
        >
          <p class="large-model-message">
            Your model is large and may take a while to generate. Please be patient.
          </p>
        </div>
      </div>
      <div v-else-if="contentStore.stlLoadingState === STLLoadingState.Succeed">
        <STLPreviewWindow />
        <div class="modal-footer mt-3">
          <div class="d-flex justify-content-center gap-3">
            <div class="dropdown custom-hover-dropdown">
              <button class="btn btn-primary dropdown-toggle" type="button" id="downloadDropdown" aria-expanded="false">
                Download
              </button>
              <ul class="dropdown-menu shadow" aria-labelledby="downloadDropdown">
                <li><a class="dropdown-item" href="#" @click.prevent="downloadSTL()">Download STL</a></li>
                <li>
                  <a class="dropdown-item d-flex align-items-center justify-content-between" href="#" @click.prevent="!isExportingStep && $emit('export-step')">
                    <span :class="{'text-muted': isExportingStep}">Download STEP</span>
                    <span v-if="isExportingStep" class="spinner-border spinner-border-sm text-primary ms-2" role="status" aria-hidden="true"></span>
                  </a>
                </li>
              </ul>
            </div>
            <button
                v-if="contentStore.hasAIExtraFiles"
                class="btn btn-outline-primary"
                @click="downloadAIExtras()"
            >
              Download Supplementary
            </button>
          </div>
        </div>
      </div>
      <div v-else-if="contentStore.stlLoadingState === STLLoadingState.Fail">
        <div class="alert alert-danger" role="alert">
          Warning: STL file generation has failed. This may be due to invalid design parameters or the design file exceeding the server processing time limit. Please check your design parameters or consider reducing the generation precision.
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import STLPreviewWindow from "./STLPreviewWindow.vue";
import { useContentStore } from "@/stores/content";
import { defineComponent, computed } from "vue";
import { STLLoadingState } from "@/library/other";
import { SlicerService } from '@/services/slicerService';
import { EdgeDetectionService } from '@/services/edgeDetectionService';
import { VoronoiService } from '@/services/voronoiService';


export default defineComponent({
  emits: ['export-step'],
  props: {
    isExportingStep: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const contentStore = useContentStore();

    const loadingText = computed(() => {
      if (contentStore.isAIProcessing) {
        return 'AI processing...';
      } else {
        return 'generating output file';
      }
    });

    const downloadSTL = () => {
      try {
        const textBlob = new Blob([contentStore.stlData], {
          type: "text/plain"
        });
        const a = document.createElement("a");
        const suffix = contentStore.userSelectedBinaryFormat ? "_binary" : "_ascii";
        a.download = `${contentStore.title}${suffix}.stl`;
        a.href = window.URL.createObjectURL(textBlob);
        a.click();
      } catch (err) {
        console.log(err);
      }
    };

    const downloadAIExtras = async () => {
      try {
        console.log('[STLPreview] Starting AI extra files download...');

        // 下载切片ZIP
        await SlicerService.downloadSlices();

        // 下载轮廓可视化
        EdgeDetectionService.downloadContourVisualizations();

        // 下载Voronoi可视化
        VoronoiService.downloadVoronoiVisualizations();

        console.log('[STLPreview] All AI extra files downloaded successfully');
      } catch (error) {
        console.error('[STLPreview] Failed to download AI extra files:', error);
        alert('Failed to download extra files: ' + error.message);
      }
    };

    return { contentStore, downloadSTL, downloadAIExtras, STLLoadingState, loadingText };
  },

  components: {
    STLPreviewWindow
  }
});
</script>

<style scoped>
#stl-preview {
  width: 100%;
  height: 100%;
  overflow: auto;
}

.loading-container {
  position: relative;
  height: 300px;
}

.loading-copy {
  position: absolute;
  top: calc(50% + 115px);
  left: 50%;
  width: min(560px, calc(100% - 32px));
  transform: translateX(-50%);
  color: #5f6873;
  text-align: center;
}

.loading-status {
  margin-top: 200px;
  margin-left: 25px;
  white-space: nowrap;
}

.large-model-message {
  margin: 0;
  color: #7a838d;
  font-size: 0.95rem;
}

.custom-hover-dropdown:hover .dropdown-menu {
  display: block;
  margin-top: 0;
}

#loader {
  width: 200px;
  height: 200px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: auto;
  filter: url('#goo');
  animation: rotate-move 2s ease-in-out infinite;
}

.dot {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: #000;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
}

.dot-3 {
  background-color: #f74d75;
  animation: dot-3-move 2s ease infinite, index 6s ease infinite;
}

.dot-2 {
  background-color: #10beae;
  animation: dot-2-move 2s ease infinite, index 6s -4s ease infinite;
}

.dot-1 {
  background-color: #ffe386;
  animation: dot-1-move 2s ease infinite, index 6s -2s ease infinite;
}

@keyframes dot-3-move {
  20% {transform: scale(1)}
  45% {transform: translateY(-18px) scale(.45)}
  60% {transform: translateY(-90px) scale(.45)}
  80% {transform: translateY(-90px) scale(.45)}
  100% {transform: translateY(0px) scale(1)}
}

@keyframes dot-2-move {
  20% {transform: scale(1)}
  45% {transform: translate(-16px, 12px) scale(.45)}
  60% {transform: translate(-80px, 60px) scale(.45)}
  80% {transform: translate(-80px, 60px) scale(.45)}
  100% {transform: translateY(0px) scale(1)}
}

@keyframes dot-1-move {
  20% {transform: scale(1)}
  45% {transform: translate(16px, 12px) scale(.45)}
  60% {transform: translate(80px, 60px) scale(.45)}
  80% {transform: translate(80px, 60px) scale(.45)}
  100% {transform: translateY(0px) scale(1)}
}

@keyframes rotate-move {
  55% {transform: translate(-50%, -50%) rotate(0deg)}
  80% {transform: translate(-50%, -50%) rotate(360deg)}
  100% {transform: translate(-50%, -50%) rotate(360deg)}
}

@keyframes index {
  0%, 100% {z-index: 3}
  33.3% {z-index: 2}
  66.6% {z-index: 1}
}


#loader1 {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 10px;
  background: #3498db;
  border-radius: 5px;
  animation: load 1.8s ease-in-out infinite;
}
#loader1:before,
#loader1:after {
  position: absolute;
  display: block;
  content: "";
  animation: load 1.8s ease-in-out infinite;
  height: 10px;
  border-radius: 5px;
}
#loader1:before {
  top: -20px;
  left: 10px;
  width: 40px;
  background: #ef4836;
}
#loader1:after {
  bottom: -20px;
  width: 35px;
  background: #f5ab35;
}

@keyframes load {
  0% {
    transform: translateX(40px);
  }
  50% {
    transform: translateX(-30px);
  }
  100% {
    transform: translateX(40px);
  }
}
</style>
